enum AuthStatus {
  LOADING = "loading",
  AUTHENTICATING = "authenticating",
  AWAITING_AUTHORIZATION = "awaiting-authorization",
  SUCCESS = "success",
  ERROR = "error",
}

import React, { useEffect, useState, useCallback } from "react";
import { useSignIn, useAuth } from "@clerk/clerk-react";
import { useParams } from "react-router-dom";
import { SignIn } from "@clerk/clerk-react";
import { Footer } from "./Footer";
import { Logo } from "@/components/ui/logo";
import { Button } from "@/components/ui/button";
import { ModeToggle } from "./mode-toggle";
import {
  CheckCircle,
  AlertCircle,
  Loader2,
  Terminal,
  Github,
} from "lucide-react";
import { FaDiscord } from "react-icons/fa6";
import { asyncHandler } from "@/utils/async-handler";

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || "https://antinomy.ai";
const API_BASE_URL = `${API_ENDPOINT}/api/v1`;

// Utility function to truncate long strings with ellipsis
const truncateString = (
  str: string,
  maxLength: number = 30,
  startChars: number = 8,
  endChars: number = 6
): string => {
  if (str.length <= maxLength) {
    return str;
  }

  const start = str.slice(0, startChars);
  const end = str.slice(-endChars);
  return `${start}...${end}`;
};

const Auth: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { isSignedIn, getToken } = useAuth();
  const { signIn, setActive } = useSignIn();

  const [status, setStatus] = useState<AuthStatus>(AuthStatus.LOADING);
  const [error, setError] = useState<string | null>(null);
  const [keyInfo, setKeyInfo] = useState<{
    apiKeyMasked?: string;
    apiKeyName?: string;
  } | null>(null);

  // Function to complete authentication with single request
  const finalizeAuthentication = useCallback(async () => {
    // Set status to show we're processing
    setStatus(AuthStatus.LOADING);

    return asyncHandler(
      async () => {
        const token = await getToken();

        if (!token) {
          throw new Error("Failed to obtain authentication token.");
        }

        // Make single PATCH request - backend will create API key internally
        const response = await fetch(
          `${API_BASE_URL}/auth/sessions/${sessionId}`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            // No body needed - backend handles key creation internally
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || "Failed to complete authentication");
        }

        const data = await response.json();
        // Backend returns masked key and key name
        setKeyInfo({
          apiKeyMasked: data.apiKeyMasked,
          apiKeyName: data.apiKeyName,
        });
        setStatus(AuthStatus.SUCCESS);

        return data;
      },
      {
        setStatus,
        setError,
        errorStatus: AuthStatus.ERROR,
      }
    );
  }, [getToken, sessionId, setStatus, setError]);

  const completeAuthentication = useCallback(async () => {
    return asyncHandler(
      async () => {
        const token = await getToken();

        if (!token) {
          throw new Error("Failed to obtain authentication token.");
        }

        // Set status to awaiting authorization instead of directly calling finalizeAuthentication
        setStatus(AuthStatus.AWAITING_AUTHORIZATION);

        return token;
      },
      {
        setStatus,
        setError,
        errorStatus: AuthStatus.ERROR,
      }
    );
  }, [getToken, setStatus, setError]);

  // After successful sign in, set this session as active
  const handleSignInComplete = useCallback(async () => {
    return asyncHandler(
      async () => {
        if (signIn?.status === "complete" && signIn?.createdSessionId) {
          await setActive({ session: signIn.createdSessionId });
          // Once active, this will trigger the isSignedIn check above
        }
      },
      {
        setStatus,
        setError,
        errorStatus: AuthStatus.ERROR,
        onError: () => setError("Failed to activate session."),
      }
    );
  }, [signIn, setActive, setStatus, setError]);

  useEffect(() => {
    // If no session ID provided, set error state
    if (!sessionId) {
      setStatus(AuthStatus.ERROR);
      setError("No session ID provided. Authentication cannot be completed.");
      return;
    }

    // Since we're already handling the sign-in redirect in App.tsx,
    // we can assume at this point that the user is signed in
    if (isSignedIn) {
      completeAuthentication();
    } else {
      // This is a fallback case, should rarely happen with our new flow
      setStatus(AuthStatus.AUTHENTICATING);
    }
  }, [isSignedIn, sessionId, completeAuthentication, setStatus, setError]);

  useEffect(() => {
    if (signIn && signIn.status === "complete") {
      handleSignInComplete();
    }
  }, [signIn, handleSignInComplete]);

  const renderStatusIcon = () => {
    switch (status) {
      case AuthStatus.LOADING:
        return <Loader2 className="w-12 h-12 text-primary animate-spin" />;
      case AuthStatus.AWAITING_AUTHORIZATION:
        return <Terminal className="w-12 h-12 text-primary" />;
      case AuthStatus.SUCCESS:
        return (
          <CheckCircle className="w-12 h-12 text-emerald-600 dark:text-emerald-400" />
        );
      case AuthStatus.ERROR:
        return (
          <AlertCircle className="w-12 h-12 text-red-600 dark:text-red-400" />
        );
      default:
        return (
          <Terminal className="w-12 h-12 text-slate-600 dark:text-slate-400" />
        );
    }
  };

  const renderStatusContent = () => {
    switch (status) {
      case AuthStatus.LOADING:
        return (
          <div className="space-y-4 text-center">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
              Preparing authentication...
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              Setting up your session securely.
            </p>
          </div>
        );

      case AuthStatus.AUTHENTICATING:
        return (
          <div className="space-y-6">
            <div className="space-y-4 text-center">
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Authentication Required
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                Please sign in to authenticate your session
              </p>
            </div>
            <div className="flex justify-center">
              <SignIn
                appearance={{
                  elements: {
                    footerAction: { display: "none" },
                  },
                }}
              />
            </div>
          </div>
        );

      case AuthStatus.AWAITING_AUTHORIZATION:
        return (
          <div className="space-y-6 text-center">
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Ready to Authorize
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                Click the button below to complete the authentication and create
                your API key.
              </p>
            </div>
            <Button
              onClick={finalizeAuthentication}
              className="w-full h-14 text-base font-medium bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 shadow-lg hover:shadow-xl transition-all duration-200 ease-out active:scale-[0.98] rounded-xl focus:ring-4 focus:ring-slate-500/20"
            >
              <Terminal className="w-5 h-5 mr-2" />
              Authorize
            </Button>
          </div>
        );

      case AuthStatus.SUCCESS:
        return (
          <div className="space-y-6 text-center">
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Authentication Successful
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                Your session has been authenticated successfully.
              </p>
              {keyInfo && (keyInfo.apiKeyName || keyInfo.apiKeyMasked) && (
                <div className="p-4 space-y-2 rounded-lg bg-slate-50 dark:bg-slate-800">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    API Key Created
                  </p>
                  {keyInfo.apiKeyName && (
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      <span className="inline-block w-12 font-medium">
                        Name:
                      </span>{" "}
                      {truncateString(keyInfo.apiKeyName, 30, 12, 8)}
                    </p>
                  )}
                  {keyInfo.apiKeyMasked && (
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      <span className="inline-block w-12 font-medium">
                        Key:
                      </span>{" "}
                      {truncateString(keyInfo.apiKeyMasked, 30, 13, 4)}
                    </p>
                  )}
                </div>
              )}
              <p className="text-sm text-slate-500 dark:text-slate-500">
                You can now close this window and return to your application.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <button
                onClick={() =>
                  window.open("https://github.com/antinomyhq/forge", "_blank")
                }
                className="relative w-full h-14 text-base font-medium bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 transition-all duration-300 ease-out active:scale-[0.97] hover:scale-[1.01] rounded-xl focus:ring-4 focus:ring-blue-500/20 hover:shadow-xl hover:shadow-blue-500/10 dark:hover:shadow-blue-400/10 group"
              >
                <span className="absolute inset-0 rounded-xl overflow-hidden">
                  <span
                    className="absolute -inset-1 rounded-xl animate-[spin_2s_linear_infinite] dark:hidden blur-sm"
                    style={{
                      background:
                        "conic-gradient(from 0deg, #2563eb, #4f46e5, #7c3aed, #2563eb)",
                    }}
                  ></span>
                  <span
                    className="absolute -inset-1 rounded-xl animate-[spin_2s_linear_infinite] hidden dark:block blur-sm"
                    style={{
                      background:
                        "conic-gradient(from 0deg, #3b82f6, #6366f1, #8b5cf6, #3b82f6)",
                    }}
                  ></span>
                  <span className="absolute inset-[2px] bg-slate-900 dark:bg-slate-100 rounded-xl"></span>
                </span>
                <span className="relative z-10 flex items-center justify-center">
                  <Github className="w-4 h-4 mr-2 transition-all duration-300 ease-out group-hover:scale-110 group-hover:rotate-12 group-hover:text-blue-400 dark:group-hover:text-blue-300 group-active:scale-95" />
                  Star our repo
                </span>
              </button>
              <button
                onClick={() =>
                  window.open("https://discord.com/invite/kRZBPpkgwq", "_blank")
                }
                className="relative w-full h-14 text-base font-medium bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 transition-all duration-300 ease-out active:scale-[0.97] hover:scale-[1.01] rounded-xl focus:ring-4 focus:ring-purple-500/20 hover:shadow-xl hover:shadow-purple-500/10 dark:hover:shadow-purple-400/10 group"
              >
                <span className="absolute inset-0 rounded-xl overflow-hidden">
                  <span
                    className="absolute -inset-1 rounded-xl animate-[spin_2.5s_linear_infinite_reverse] dark:hidden blur-sm"
                    style={{
                      background:
                        "conic-gradient(from 0deg, #7c3aed, #a855f7, #ec4899, #7c3aed)",
                    }}
                  ></span>
                  <span
                    className="absolute -inset-1 rounded-xl animate-[spin_2.5s_linear_infinite_reverse] hidden dark:block blur-sm"
                    style={{
                      background:
                        "conic-gradient(from 0deg, #8b5cf6, #a855f7, #f472b6, #8b5cf6)",
                    }}
                  ></span>
                  <span className="absolute inset-[2px] bg-slate-900 dark:bg-slate-100 rounded-xl"></span>
                </span>
                <span className="relative z-10 flex items-center justify-center">
                  <FaDiscord className="w-4 h-4 mr-2 transition-all duration-300 ease-out group-hover:scale-110 group-hover:rotate-3 group-hover:text-purple-400 dark:group-hover:text-purple-300 group-active:scale-95" />
                  Join our Discord
                </span>
              </button>
            </div>
          </div>
        );

      case AuthStatus.ERROR:
        return (
          <div className="space-y-6 text-center">
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Authentication Failed
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                {error ||
                  "An error occurred during the authentication process."}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <button
                onClick={() =>
                  window.open("https://github.com/antinomyhq/forge", "_blank")
                }
                className="relative w-full h-14 text-base font-medium bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 transition-all duration-300 ease-out active:scale-[0.97] hover:scale-[1.01] rounded-xl focus:ring-4 focus:ring-blue-500/20 hover:shadow-xl hover:shadow-blue-500/10 dark:hover:shadow-blue-400/10 group"
              >
                <span className="absolute inset-0 rounded-xl overflow-hidden">
                  <span
                    className="absolute -inset-1 rounded-xl animate-[spin_2s_linear_infinite] dark:hidden blur-sm"
                    style={{
                      background:
                        "conic-gradient(from 0deg, #2563eb, #4f46e5, #7c3aed, #2563eb)",
                    }}
                  ></span>
                  <span
                    className="absolute -inset-1 rounded-xl animate-[spin_2s_linear_infinite] hidden dark:block blur-sm"
                    style={{
                      background:
                        "conic-gradient(from 0deg, #3b82f6, #6366f1, #8b5cf6, #3b82f6)",
                    }}
                  ></span>
                  <span className="absolute inset-[2px] bg-slate-900 dark:bg-slate-100 rounded-xl"></span>
                </span>
                <span className="relative z-10 flex items-center justify-center">
                  <Github className="w-4 h-4 mr-2 transition-all duration-300 ease-out group-hover:scale-110 group-hover:rotate-12 group-hover:text-blue-400 dark:group-hover:text-blue-300 group-active:scale-95" />
                  Star our repo
                </span>
              </button>
              <button
                onClick={() =>
                  window.open("https://discord.com/invite/kRZBPpkgwq", "_blank")
                }
                className="relative w-full h-14 text-base font-medium bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 transition-all duration-300 ease-out active:scale-[0.97] hover:scale-[1.01] rounded-xl focus:ring-4 focus:ring-purple-500/20 hover:shadow-xl hover:shadow-purple-500/10 dark:hover:shadow-purple-400/10 group"
              >
                <span className="absolute inset-0 rounded-xl overflow-hidden">
                  <span
                    className="absolute -inset-1 rounded-xl animate-[spin_2.5s_linear_infinite_reverse] dark:hidden blur-sm"
                    style={{
                      background:
                        "conic-gradient(from 0deg, #7c3aed, #a855f7, #ec4899, #7c3aed)",
                    }}
                  ></span>
                  <span
                    className="absolute -inset-1 rounded-xl animate-[spin_2.5s_linear_infinite_reverse] hidden dark:block blur-sm"
                    style={{
                      background:
                        "conic-gradient(from 0deg, #8b5cf6, #a855f7, #f472b6, #8b5cf6)",
                    }}
                  ></span>
                  <span className="absolute inset-[2px] bg-slate-900 dark:bg-slate-100 rounded-xl"></span>
                </span>
                <span className="relative z-10 flex items-center justify-center">
                  <FaDiscord className="w-4 h-4 mr-2 transition-all duration-300 ease-out group-hover:scale-110 group-hover:rotate-3 group-hover:text-purple-400 dark:group-hover:text-purple-300 group-active:scale-95" />
                  Join our Discord
                </span>
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100/50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px]" />

      {/* Header */}
      <header className="relative z-10 flex items-center h-16 gap-2 border-b shrink-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-4 px-6">
          <Logo
            className="h-7 w-auto max-w-[140px]"
            onClick={() => (window.location.href = "/")}
          />
          <div className="w-px h-4 bg-slate-300 dark:bg-slate-700" />
          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
            Authentication
          </span>
        </div>
        <div className="px-6 ml-auto">
          <ModeToggle />
        </div>
      </header>

      <div className="relative flex flex-col items-center justify-center flex-1 px-6 py-16">
        <div className="w-full max-w-md">
          {/* Primary Focus - Auth Card */}
          <div className="relative mb-8 duration-700 delay-200 animate-in fade-in-50 slide-in-from-bottom-4">
            {/* Enhanced glow effect for focus */}
            <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/15 via-indigo-500/15 to-purple-500/15 rounded-3xl blur-2xl opacity-60 animate-pulse"></div>

            {/* Main form container - Hero element */}
            <div className="relative border shadow-2xl bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl rounded-2xl border-slate-200/80 dark:border-slate-700/80 shadow-slate-900/10 dark:shadow-black/20">
              <div className="p-10">
                <div className="flex flex-col gap-8">
                  {/* Header section matching LoginForm */}
                  <div className="space-y-3 text-center duration-700 animate-in fade-in-50 slide-in-from-top-4">
                    <div className="flex justify-center mb-4">
                      {renderStatusIcon()}
                    </div>
                  </div>

                  {/* Content section matching LoginForm spacing */}
                  <div className="space-y-4 duration-700 delay-200 animate-in fade-in-50 slide-in-from-bottom-4">
                    {renderStatusContent()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Auth;
